import {
  ArrayNotEmpty,
  IsBoolean,
  IsDateString,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  MinLength,
  ValidateNested,
} from 'class-validator';
import { ApiProperty } from '@nestjs/swagger';
import { Type } from 'class-transformer';
import type { ApiServerEntityIDType } from '../../types';
import { CHILD_MAX_AGE, CHILD_MIN_AGE } from '../../const/common';

const MIN_LENGTH = 5;
const MAX_LENGTH = 255;

/**
 * Represents a child involved in the deal.
 * Used for pricing and eligibility calculations.
 */
export class DealChildCreationDTO {
  @ApiProperty({
    description: `Child's age in years (between ${CHILD_MIN_AGE} and ${CHILD_MAX_AGE})`,
    example: 6,
    minimum: CHILD_MIN_AGE,
    maximum: CHILD_MAX_AGE,
  })
  @IsInt()
  @Min(CHILD_MIN_AGE)
  @Max(CHILD_MAX_AGE)
  age: number;
}

/**
 * DTO for creating a new deal.
 */
export class DealCreationDTO {
  @ApiProperty({ description: 'Full address of the deal', example: '123 Main St, Moscow' })
  @IsString()
  @MinLength(MIN_LENGTH, {
    message: 'Адрес должен быть длиннее 5 символов',
  })
  @MaxLength(MAX_LENGTH, {
    message: 'Адрес должен быть короче 255 символов',
  })
  address: string;

  @ApiProperty({ description: 'Location or region', example: '53.337559,83.658512' })
  @IsString()
  @IsOptional()
  location?: string;

  @ApiProperty({
    description: `Deal description (between ${MIN_LENGTH} and ${MAX_LENGTH} characters)`,
    example: 'Weekend care for two children',
  })
  @IsString()
  @MinLength(MIN_LENGTH, {
    message: 'Описание должно быть длиннее 5 символов',
  })
  description: string;

  @ApiProperty({ description: 'Whether any child has disabilities', example: false })
  @IsBoolean()
  childWithDisabilities: boolean;

  @ApiProperty({ description: 'Whether any child is sick', example: true })
  @IsBoolean()
  sickChild: boolean;

  @ApiProperty({ description: 'Start date in ISO format', example: '2025-09-03T09:00:00.000Z' })
  @IsDateString(undefined, {
    message: 'Выберите время',
  })
  startedAt: string;

  @ApiProperty({ description: 'End date in ISO format', example: '2025-09-05T18:00:00.000Z' })
  @IsDateString(undefined, {
    message: 'Выберите время',
  })
  endedAt: string;

  @ApiProperty({
    description: 'List of children involved',
    type: DealChildCreationDTO,
    isArray: true,
    example: [{ age: 5 }, { age: 8 }],
  })
  @ValidateNested({ each: true })
  @Type(() => DealChildCreationDTO)
  @ArrayNotEmpty()
  children: DealChildCreationDTO[];

  @ApiProperty({ description: 'Dialog ID for the deal', example: 1024 })
  @IsNumber()
  dialogId: number;
}

/**
 * Extends DealCreationDTO with target client ID.
 */
export class DealCreationWithClientRequest extends DealCreationDTO {
  @ApiProperty({ description: 'Target client ID', example: 456789 })
  @IsNumber()
  targetClientId: ApiServerEntityIDType;
}

/**
 * DTO for calculating deal price.
 */
export class DealPriceRequest {
  @ApiProperty({ description: 'Client ID', example: 123456 })
  @IsNumber()
  clientId: ApiServerEntityIDType;

  @ApiProperty({
    description: 'Children involved',
    type: DealChildCreationDTO,
    isArray: true,
    example: [{ age: 4 }],
  })
  @ValidateNested({ each: true })
  @Type(() => DealChildCreationDTO)
  @ArrayNotEmpty()
  children: DealChildCreationDTO[];

  @ApiProperty({ description: 'Start date', example: '2025-09-03T09:00:00.000Z' })
  @IsDateString()
  startedAt: string;

  @ApiProperty({ description: 'End date', example: '2025-09-05T18:00:00.000Z' })
  @IsDateString()
  endedAt: string;
}
